import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Truck } from "lucide-react";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white text-slate-900 p-6">
      <form onSubmit={submit} className="w-full max-w-md" data-testid="forgot-form">
        <Brand />
        <h1 className="font-display font-black text-3xl uppercase tracking-tight">Reset Password</h1>
        <p className="text-sm text-slate-500 mt-2">Enter your email and we'll send you a reset link.</p>
        <div className="mt-6">
          <label className="hs-overline">Email</label>
          <input className="hs-input mt-2" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
        </div>
        {error && <div className="text-sm text-red-600 mt-3" data-testid="forgot-error">{error}</div>}
        {sent && (
          <div className="mt-4 p-3 border border-blue-700/40 bg-blue-700/5 text-xs" data-testid="forgot-success">
            <div className="hs-overline text-blue-700 mb-1">CHECK YOUR EMAIL</div>
            If an account exists for that email, we&apos;ve sent a password reset link. The link expires in 1 hour.
          </div>
        )}
        <button className="hs-btn-primary w-full mt-6" disabled={loading} data-testid="forgot-submit-button">
          {loading ? "SENDING…" : "SEND RESET LINK"}
        </button>
        <div className="mt-6 text-sm">
          <Link to="/login" className="text-slate-500 hover:text-slate-900" data-testid="forgot-back-link">← Back to sign in</Link>
        </div>
      </form>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pwd });
      toast.success("Password updated");
      nav("/login");
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white text-slate-900 p-6">
      <form onSubmit={submit} className="w-full max-w-md" data-testid="reset-form">
        <Brand />
        <h1 className="font-display font-black text-3xl uppercase tracking-tight">Set New Password</h1>
        <div className="mt-6">
          <label className="hs-overline">New Password</label>
          <input className="hs-input mt-2" type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-password-input" />
        </div>
        {error && <div className="text-sm text-red-600 mt-3" data-testid="reset-error">{error}</div>}
        <button className="hs-btn-primary w-full mt-6" disabled={loading || !token} data-testid="reset-submit-button">
          {loading ? "UPDATING…" : "UPDATE PASSWORD"}
        </button>
      </form>
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center mb-8">
      <img src="/logo.png" alt="Crystal Logistics Services" className="h-24 w-auto" />
    </div>
  );
}
